export const todayStr = (now = new Date()) => {
    const d = new Date(now);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Multi-token search scoring for the global search UI. Given a normalized query and one or more
 * candidate fields (any strings), returns a rank:
 *   3 → the full phrase appears verbatim (case-insensitive substring)
 *   2 → EVERY token appears somewhere across the joined fields ("AND" match)
 *   1 → at least one token appears ("OR" match)
 *   0 → nothing matches
 * A normalized-alphanumeric fallback also counts as rank 1 so queries with punctuation still hit
 * (e.g. searching "back-pain" still finds "back pain").
 * Callers filter with `>= 1` and sort desc so phrase matches float to the top.
 */
export const searchScore = (query, ...fields) => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return 0;
    const hay = fields.map((f) => String(f || "").toLowerCase()).join(" \u241E ");
    if (!hay.trim()) return 0;
    if (hay.includes(q)) return 3;
    const tokens = Array.from(new Set(q.split(/\s+/).filter((t) => t.length > 0)));
    if (tokens.length === 0) return 0;
    const hits = tokens.filter((t) => hay.includes(t)).length;
    if (hits === tokens.length && tokens.length > 1) return 2;
    if (hits > 0) return 1;
    // Punctuation-tolerant fallback: strip non-alphanumerics on both sides
    const qNorm = q.replace(/[^a-z0-9]+/g, "");
    const hayNorm = hay.replace(/[^a-z0-9]+/g, "");
    if (qNorm.length > 0 && hayNorm.includes(qNorm)) return 1;
    return 0;
};

export const dateFromStr = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

export const daysDiff = (dateStr) => {
    const target = dateFromStr(dateStr);
    if (!target) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

/** Days-from-today for a task's next occurrence. Mirrors Screensaver's `dayOffsetOf`.
 *  Recurring tasks resolve to the days-until-next-occurrence (0 if today matches).
 *  Non-recurring return their dated offset. `null` for undated/untriggerable tasks.
 *  Honors `task.instance_overrides` — postponed occurrences shift to their `to` date,
 *  the original `from` day is skipped, and any explicit `to` days are considered. */
export const taskDayOffset = (task) => {
    if (!task) return null;
    if (task.type === "recurring" && task.interval) {
        // Same anchor-fallback rule as taskOccursOnIso / isRecurringMissed.
        let anchorSource = task.date;
        if (!anchorSource) {
            if (!task.created_at) return null;
            const c = new Date(task.created_at);
            anchorSource = `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}-${String(c.getDate()).padStart(2, "0")}`;
        }
        const overrides = effectiveOverrides(task);
        const skipSet = new Set(overrides.map((ov) => ov.from));
        const extraOffsets = overrides
            .map((ov) => daysDiff(ov.to))
            .filter((d) => d !== null && d >= 0);
        const isoAt = (offset) => {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() + offset);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        };
        // Walk forward up to 400 days (covers yearly recurrences). Uses taskOccursOnIso so
        // calendar intervals (30 = monthly, 365 = yearly) resolve correctly instead of drifting.
        for (let off = 0; off < 400; off++) {
            const iso = isoAt(off);
            if (extraOffsets.includes(off)) return off;             // explicit postponed target
            if (skipSet.has(iso)) continue;                          // postponed away
            if (taskOccursOnIso({ ...task, date: anchorSource, instance_overrides: [] }, iso)) return off;
        }
        return null;
    }
    if (!task.date) return null;
    return daysDiff(task.date);
};

/** Returns the list of ISO date strings (YYYY-MM-DD in LOCAL time) for every scheduled beat of
 *  a recurring task that is:
 *    · in the past (its scheduled beatMs < now),
 *    · NOT present in `task.completions[]` (per-occurrence check-ins are authoritative),
 *    · NOT satisfied by the legacy `task.completed_at` baseline (any beat whose ms ≤ completed_at
 *      is treated as done — this lets rows created before the completions[] refactor keep their
 *      historical done state without requiring a full backfill).
 *
 *  CRITICAL DESIGN NOTES:
 *   · The walk does NOT stop early on a completed beat. That was the old boolean-only bug —
 *     if today was checked in, the walk returned false and every older miss became invisible.
 *     Now we skip over completed beats and keep looking backward so misses persist.
 *   · Occurrence identity is the beat's LOCAL calendar date. `new Date(y, m-1, d, hh, mm)` builds
 *     in local time; `todayStr()` and `beatIso` here use `getFullYear/getMonth/getDate` (local),
 *     matching `completions[]` writes elsewhere. A 10 PM local beat therefore stays tied to its
 *     local day even when UTC has already rolled over.
 *   · Walks up to 400 days back but caps returned misses at 60 (safety — long-dormant tasks
 *     shouldn't blow up the overdue list; user can dismiss legacy state via check-off).
 *
 *  Returns oldest-first (chronological). Non-recurring / interval-less tasks return []. */
/** Occurrence-level snooze for recurring tasks. `task.occurrence_snoozes` is `{ iso → untilMs }`.
 *  True iff that specific beat is snoozed at `nowMs`. Task-level `snoozed_until` is a
 *  one-shot-task concept and is deliberately IGNORED for recurring cadences. */
export const isOccurrenceSnoozed = (task, iso, nowMs = Date.now()) => {
    const until = task?.occurrence_snoozes?.[iso];
    return typeof until === "number" && until > nowMs;
};

/** Task-level snooze gate shared by every list. Recurring cadences are never hidden wholesale —
 *  their snoozes live per-occurrence (see isOccurrenceSnoozed). */
export const isTaskSnoozed = (task, nowMs = Date.now()) => {
    if (!task) return false;
    if (task.type === "recurring" && task.interval) return false;
    return !!task.snoozed_until && task.snoozed_until >= nowMs;
};

/** Local instant from which an obligation displayed on `iso` is ACTIVE (its time, or the start
 *  of its day when date-only). Used for the legacy `completed_at` baseline. */
export const occurrenceStartMs = (iso, time) => {
    const [y, m, d] = String(iso).split("-").map(Number);
    if (time) { const [hh, mm] = time.split(":").map(Number); return new Date(y, m - 1, d, hh, mm).getTime(); }
    return new Date(y, m - 1, d).getTime();
};

/** Local instant at which an obligation displayed on `iso` becomes OVERDUE. Timed → its time.
 *  Date-only → the START of the NEXT local day: the whole calendar day is "today"; a Sep 17
 *  date-only beat is overdue from Sep 18 00:00 local — never from midnight at the start of Sep 17. */
export const occurrenceDueMs = (iso, time) => {
    if (time) return occurrenceStartMs(iso, time);
    const [y, m, d] = String(iso).split("-").map(Number);
    return new Date(y, m - 1, d + 1).getTime();
};

export const getMissedOccurrences = (task, now = new Date()) => {
    if (!task || task.type !== "recurring" || !task.interval) return [];
    if (!task.date && !task.created_at) return [];
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    // No "today < anchor" early exit and no "d < anchor" stop: a FUTURE natural beat moved EARLIER
    // than the series anchor (Sep22 → Sep15) is a real past obligation. The scan stays bounded
    // (400 display days) and getOccurrencesOnIso decides what lands on each day.
    const completedAt = task.completed_at || 0;
    const completions = task.completions || [];
    const nowMs = now.getTime();
    const missed = [];
    for (let back = 0; back <= 400; back++) {
        const d = new Date(start); d.setDate(start.getDate() - back);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        for (const occ of getOccurrencesOnIso(task, iso)) {
            const beatMs = occurrenceDueMs(iso, occ.time);          // timed: its time · date-only: next local day
            if (beatMs > nowMs) continue;                          // not yet due (date-only: its day is not over)
            if (completions.includes(occ.key)) continue;            // this obligation is done
            if (completedAt && completedAt >= occurrenceStartMs(iso, occ.time)) continue; // legacy pre-completions baseline
            if (isOccurrenceSnoozed(task, occ.key, nowMs)) continue; // this obligation is snoozed
            missed.push({ key: occ.key, beatMs });
        }
        if (missed.length >= 60) break;                     // safety cap
    }
    // Ordered by EFFECTIVE due instant (display day + effective time), oldest first — never by source ISO.
    return missed.sort((a, b) => a.beatMs - b.beatMs || a.key.localeCompare(b.key)).map((m) => m.key);
};

/** SOURCE key(s) a nag acts on: the exact key the push carried (`_occKey`), else the latest
 *  outstanding obligation, else today's. Both "mark done" and "snooze" MUST use this. */
export const nagSourceIsos = (task, now = new Date()) => {
    if (!task || task.type !== "recurring" || !task.interval) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(task._occKey || "")) return [task._occKey];
    const misses = getMissedOccurrences(task, now);
    return [misses.length ? misses[misses.length - 1] : todayStr()];
};

/** Occurrence-scope edit plan. Identity = SOURCE key; a time-only edit keeps the beat where it
 *  currently SITS (display day) and only changes `to_time` — it never snaps back to the source day. */
export const planOccurrenceEdit = (task, { sourceIso, displayIso, date, dateChanged, time }) => {
    const fromIso = sourceIso || todayStr();
    const currentDisplay = displayIso || effectiveOverrides(task).find((o) => o.from === fromIso)?.to || fromIso;
    const toIso = dateChanged ? (date || currentDisplay) : currentDisplay;
    return { fromIso, toIso, toTime: time || null };
};

/** True iff a RECURRING task has at least one missed past beat. Kept as a thin wrapper around
 *  `getMissedOccurrences` so all downstream filters use one truth. */
export const isRecurringMissed = (task) => getMissedOccurrences(task).length > 0;

/** Full per-occurrence history for a recurring task. Returns beats in `[now-lookback, now+lookahead]`
 *  ordered NEWEST first, each entry classified into a stable state:
 *    · "completed" — beat's ISO is in `completions[]` (has `completed_at_ms` when known from
 *      `completion_times[iso]`; otherwise `null` for legacy check-ins that predate per-occurrence
 *      timestamps), OR the beat is on/before the frozen legacy baseline `completed_at`.
 *    · "postponed" — beat's ISO is a `from` entry in `instance_overrides` (moved to `movedTo`).
 *    · "missed"    — beat's ms is strictly in the past, not completed, not postponed, not baselined.
 *    · "pending"   — today's beat that hasn't been completed yet (whether or not its scheduled
 *                    time has passed — the user can still check it in tonight).
 *    · "upcoming"  — future scheduled beat (after today's local end-of-day).
 *
 *  Extra occurrences added by postponements (their `to` day) are also included with a
 *  `movedFrom` marker. Time overrides (`to_time`) are honoured in `beatMs`.
 *
 *  This is the source of truth for the TaskEditor's Occurrence History section — never walks
 *  more than ~90 days of context to keep the render cost bounded on long-lived cadences. */
export const getOccurrenceHistory = (task, now = new Date(), lookbackDays = 30, lookaheadDays = 7) => {
    if (!task || task.type !== "recurring" || !task.interval) return [];
    const today = new Date(now); today.setHours(0, 0, 0, 0);
    const completions = task.completions || [];
    const completionTimes = task.completion_times || {};
    const completedAt = task.completed_at || 0;
    const nowMs = now.getTime();
    const overrides = effectiveOverrides(task);
    const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const rows = [];
    for (let off = -lookbackDays; off <= lookaheadDays; off++) {
        const d = new Date(today); d.setDate(today.getDate() + off);
        const displayIso = isoOf(d);
        // ONE row per obligation landing here — a moved beat and a native beat stay separate.
        for (const occ of getOccurrencesOnIso(task, displayIso)) {
            const sourceIso = occ.key;
            const ov = overrides.find((o) => o.from === sourceIso);
            const time = occ.time || null;
            const beatMs = occurrenceStartMs(displayIso, time);   // active-from instant (legacy baseline)
            const dueMs = occurrenceDueMs(displayIso, time);      // date-only beats are due at the end of their day
            let state, completedAtMs = null;
            if (completions.includes(sourceIso)) { state = "completed"; completedAtMs = completionTimes[sourceIso] || null; }
            else if (completedAt && beatMs <= completedAt) state = "completed";
            else if (dueMs > nowMs) state = d.getTime() === today.getTime() ? "pending" : "upcoming";
            else if (isOccurrenceSnoozed(task, sourceIso, nowMs)) state = "snoozed";
            else state = "missed";
            rows.push({
                key: `${task.id}:${sourceIso}`, sourceIso, iso: sourceIso, displayIso, time, beatMs, state, completedAtMs,
                movedFrom: ov ? ov.from : undefined, movedTo: ov ? ov.to : undefined,
                ...(state === "snoozed" ? { snoozedUntil: (task.occurrence_snoozes || {})[sourceIso] } : {}),
            });
        }
    }
    return rows.sort((a, b) => b.beatMs - a.beatMs);
};

/** True iff `task` has a scheduled occurrence on the given ISO date. Honors overrides — the
 *  `from` day of a postponed occurrence returns false, the `to` day returns true. */
/** Natural cadence only — ignores instance_overrides. True iff the series' own rhythm lands on `iso`. */
export const isNaturalOccurrence = (task, iso) => {
    if (!task || !iso || task.type !== "recurring" || !task.interval) return false;
    let anchorSource = task.date;
    if (!anchorSource) {
        if (!task.created_at) return false;
        const c = new Date(task.created_at);
        anchorSource = `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}-${String(c.getDate()).padStart(2, "0")}`;
    }
    return _naturalMath(task, anchorSource, iso);
};

/** Every distinct OBLIGATION shown on `iso` for a recurring task. Identity (`key`) is the beat's
 *  SOURCE date — a beat moved Fri→Sat keeps key "…-11" while Saturday's own beat is "…-12", so
 *  the two never collapse and each is completed/snoozed independently via completions[key] /
 *  occurrence_snoozes[key]. `time` honours the override's to_time. */
/** Canonical override audit. INVARIANT: exactly one effective entry per legitimate NATURAL source.
 *  exact duplicates fold · chains collapse to SOURCE → final display · dangling nodes whose `from`
 *  is a former display date vanish · two destinations for one source = OVERRIDE_CONFLICT (flagged,
 *  never guessed: display uses the last-written entry marked `conflict: true`; cleanup refuses it).
 *  Mirrors backend `_override_audit`. */
const ovVersioned = (o) => Number(o?.v || 0) >= 2; // provenance: written by the source-aware engine
export const OV_ACTORS = ["user", "repair", "import", "ai"];
export const OV_REASONS = ["postpone", "editor", "quick_bump", "repair_canonical", "import_canonical"];
/** Mirror of backend `_override_provenance` — display label only, never written back. */
export const overrideProvenance = (o) => {
    const versioned = ovVersioned(o);
    if (versioned && OV_ACTORS.includes(o?.by) && OV_REASONS.includes(o?.reason)) return { by: o.by, reason: o.reason, kind: "v2" };
    const kind = versioned ? "legacy_v2" : "legacy";
    return { by: "unknown", reason: kind, kind };
};

/** Mirror of backend `_override_audit`. Anything history cannot prove is flagged, never guessed:
 *  CONFLICT · CYCLE · AMBIGUOUS_SHARED_INTERMEDIATE · AMBIGUOUS_NATURAL_LINK (legacy, no v>=2). */
export const overrideAudit = (task) => {
    const rawAll = (task?.instance_overrides || []).filter((o) => o?.from && o?.to);
    const kinds = new Set();
    const raw = [];
    const seenSig = new Set();
    for (const o of rawAll) {
        const sig = `${o.from}|${o.to}|${o.to_time || ""}`;
        if (seenSig.has(sig)) { kinds.add("EXACT_DUPLICATE"); continue; }
        seenSig.add(sig); raw.push(o);
    }
    const byFrom = new Map(); const byTo = new Map();
    for (const o of raw) {
        if (!byFrom.has(o.from)) byFrom.set(o.from, []); byFrom.get(o.from).push(o);
        if (!byTo.has(o.to)) byTo.set(o.to, []); byTo.get(o.to).push(o);
    }
    const ambiguities = []; const flagged = new Set(); const excludedFroms = new Set();
    const key = (o) => `${o.from}>${o.to}`;
    for (const o of raw) {
        if (excludedFroms.has(o.from)) continue;
        const path = [o]; const seen = new Set([o.from]); let cur = o;
        for (;;) {
            const nxts = byFrom.get(cur.to) || [];
            if (seen.has(cur.to)) {
                kinds.add("CYCLE");
                ambiguities.push({ kind: "CYCLE", source: o.from, nodes: [...seen].sort() });
                for (const p of path) excludedFroms.add(p.from);
                break;
            }
            if (nxts.length !== 1) break;
            seen.add(cur.to); cur = nxts[0]; path.push(cur);
        }
    }
    for (const [node, arrivals] of byTo) {
        if (excludedFroms.has(node) || arrivals.some((a) => excludedFroms.has(a.from))) continue;
        if (arrivals.length >= 2 && byFrom.has(node) && !isNaturalOccurrence(task, node)) {
            kinds.add("AMBIGUOUS_SHARED_INTERMEDIATE");
            ambiguities.push({ kind: "AMBIGUOUS_SHARED_INTERMEDIATE", node, sources: arrivals.map((a) => a.from).sort(), onward: byFrom.get(node).map((n) => n.to).sort() });
            for (const a of arrivals) flagged.add(key(a));
            excludedFroms.add(node);
        }
    }
    for (const o of raw) {
        if (excludedFroms.has(o.from) || flagged.has(key(o))) continue;
        if (isNaturalOccurrence(task, o.to)) {
            const onward = byFrom.get(o.to) || [];
            if (onward.length && !(ovVersioned(o) && onward.every(ovVersioned))) {
                kinds.add("AMBIGUOUS_NATURAL_LINK");
                ambiguities.push({ kind: "AMBIGUOUS_NATURAL_LINK", source: o.from, via: o.to, destinations: onward.map((n) => n.to).sort() });
                flagged.add(key(o)); for (const n of onward) flagged.add(key(n));
            }
        }
    }
    const linkFroms = new Set();
    const branchConflicts = new Map();
    let collapsed = [];
    for (const o of raw) {
        if (linkFroms.has(o.from) || excludedFroms.has(o.from)) continue;
        const cur = { ...o };
        if (flagged.has(key(o))) { cur.ambiguous = true; collapsed.push(cur); continue; }
        const seen = new Set([cur.from]);
        for (;;) {
            const nxts = byFrom.get(cur.to) || [];
            if (!nxts.length || isNaturalOccurrence(task, cur.to) || excludedFroms.has(cur.to)) break;
            const distinct = new Set(nxts.map((n) => `${n.to}|${n.to_time || ""}`));
            if (distinct.size > 1) {
                kinds.add("CONFLICT");
                if (!branchConflicts.has(o.from)) branchConflicts.set(o.from, new Set());
                for (const n of nxts) { branchConflicts.get(o.from).add(n.to); linkFroms.add(n.from); }
                break;
            }
            const nxt = nxts[nxts.length - 1];
            if (seen.has(nxt.to)) {
                kinds.add("CYCLE");
                ambiguities.push({ kind: "CYCLE", source: o.from, nodes: [...seen, nxt.to].sort() });
                linkFroms.add(nxt.from); excludedFroms.add(o.from);
                break;
            }
            kinds.add("CHAIN"); linkFroms.add(nxt.from); seen.add(nxt.to);
            cur.to = nxt.to; if (nxt.to_time) cur.to_time = nxt.to_time;
        }
        if (!excludedFroms.has(o.from)) collapsed.push(cur);
    }
    collapsed = collapsed.filter((o) => !linkFroms.has(o.from));
    const orphans = collapsed.filter((o) => !isNaturalOccurrence(task, o.from));
    if (orphans.length) kinds.add("ORPHAN");
    collapsed = collapsed.filter((o) => isNaturalOccurrence(task, o.from));
    const perSrc = new Map();
    for (const o of collapsed) { if (!perSrc.has(o.from)) perSrc.set(o.from, []); perSrc.get(o.from).push(o); }
    const canonical = []; const conflicts = [];
    for (const [src, entries] of perSrc) {
        const dests = new Set(entries.map((e) => `${e.to}|${e.to_time || ""}`));
        const branch = branchConflicts.get(src);
        if (dests.size > 1 || branch) {
            kinds.add("CONFLICT");
            const all = new Set(branch ? [...branch] : []);
            if (dests.size > 1) for (const e of entries) all.add(e.to);
            conflicts.push({ source: src, destinations: [...all].sort() });
            canonical.push({ ...entries[entries.length - 1], conflict: true });
        } else {
            if (entries.length > 1) kinds.add("EXACT_DUPLICATE");
            canonical.push({ ...entries[0] });
        }
    }
    canonical.sort((a, b) => a.from.localeCompare(b.from));
    const sig = (l) => l.map((o) => `${o.from}|${o.to}|${o.to_time || ""}`).sort().join(";");
    const changed = sig(rawAll) !== sig(canonical);
    const blocked = conflicts.length > 0 || ambiguities.length > 0;
    const provenance = Object.fromEntries(canonical.map((o) => [o.from, overrideProvenance(o)]));
    return { raw: rawAll, canonical, kinds: [...kinds].sort(), conflicts, ambiguities, provenance, blocked, changed, safeToRepair: changed && !blocked };
};

export const effectiveOverrides = (task) => overrideAudit(task).canonical;

/** Legitimate obligation identity: a natural beat, or the SOURCE of an effective override. */
export const isSourceKey = (task, iso) => {
    if (!task || !iso) return false;
    if (task.type !== "recurring" || !task.interval) return task.date === iso;
    return effectiveOverrides(task).some((o) => o.from === iso) || isNaturalOccurrence(task, iso);
};

/** ONE task object per id — used on every state write (fetch, SSE, create, PATCH, optimistic). */
export const normalizeTasksById = (list) => {
    const byId = new Map(); const dupes = [];
    for (const t of list || []) {
        if (!t?.id) continue;
        if (byId.has(t.id)) dupes.push(t.id);
        byId.set(t.id, t); // last write wins (latest server echo)
    }
    if (dupes.length && typeof console !== "undefined") console.warn("[tasks] duplicate ids collapsed:", [...new Set(dupes)]);
    return [...byId.values()];
};

export const getOccurrencesOnIso = (task, iso) => {
    if (!task || !iso) return [];
    if (task.type !== "recurring" || !task.interval) return task.date === iso ? [{ key: iso, displayIso: iso, movedFrom: null, time: task.time || null }] : [];
    const overrides = effectiveOverrides(task);
    const out = [];
    if (isNaturalOccurrence(task, iso) && !overrides.some((ov) => ov.from === iso)) {
        out.push({ key: iso, displayIso: iso, movedFrom: null, time: task.time || null });
    }
    for (const ov of overrides) {
        if (ov.to === iso && ov.from) out.push({ key: ov.from, displayIso: iso, movedFrom: ov.from, time: ov.to_time || task.time || null });
    }
    // Oldest obligation first (a beat moved here from an earlier day precedes the native beat).
    return [...new Map(out.map((o) => [o.key, o])).values()].sort((a, b) => a.key.localeCompare(b.key))
};

export const taskOccursOnIso = (task, iso) => {
    if (!task || !iso) return false;
    if (task.type === "recurring" && task.interval) {
        return getOccurrencesOnIso(task, iso).length > 0;
    }
    return task.date === iso;
};

const _naturalMath = (task, anchorSource, iso) => {
    {
        try {
            const [ay, am, ad] = anchorSource.split("-").map(Number);
            const [ty, tm, td] = iso.split("-").map(Number);
            const anchor = new Date(ay, am - 1, ad);
            const target = new Date(ty, tm - 1, td);
            if (target < anchor) return false;
            // CALENDAR-AWARE INTERVALS: `30` and `365` are used by the AI (and UI chips) as
            // shortcuts for "every month" and "every year". Using them as raw day-counts
            // drifts the anchor day (e.g. Jul 6 + 30 = Aug 5, not Aug 6). Handle these two
            // as proper calendar recurrences that hit the same day-of-month / month+day-of-month.
            // "month_end" is the string sentinel for "last calendar day of every month" — fires
            // on Feb 28 (or 29 leap), Apr 30, Aug 31, etc. — never drifts.
            if (task.interval === "month_end") {
                const lastDayOfTargetMonth = new Date(ty, tm, 0).getDate();
                return td === lastDayOfTargetMonth;
            }
            if (task.interval === 30) {
                if (td === ad) return true;
                // Anchor on 31st in a shorter month → fire on the last day (e.g. Feb 28/29).
                const lastDayOfTargetMonth = new Date(ty, tm, 0).getDate();
                if (ad > lastDayOfTargetMonth && td === lastDayOfTargetMonth) return true;
                return false;
            }
            if (task.interval === 365) {
                if (tm === am && td === ad) return true;
                // Feb 29 → Feb 28 in non-leap years.
                if (am === 2 && ad === 29 && tm === 2 && td === 28) {
                    const isLeap = (ty % 4 === 0 && ty % 100 !== 0) || (ty % 400 === 0);
                    if (!isLeap) return true;
                }
                return false;
            }
            const delta = Math.round((target - anchor) / 86_400_000);
            return delta >= 0 && delta % task.interval === 0;
        } catch { return false; }
    }
};

/** For a task shown on `iso`, returns the ISO date it was MOVED FROM (if this is a postponed
 *  occurrence's target day) or `null` otherwise. Used to render the "moved from … undo" chip. */
export const undoFromForIso = (task, iso) => {
    if (!task || !iso) return null;
    const ov = effectiveOverrides(task).find((o) => o.to === iso);
    return ov ? ov.from : null;
};

/** Effective time for a recurring task on a specific date. Honors per-occurrence overrides
 *  (`{from, to, to_time?}`) — if this iso is the `to` of an override that carries a `to_time`,
 *  that time wins over `task.time`. Otherwise falls back to the task's default `time`. */
export const effectiveTimeForIso = (task, iso) => {
    if (!task) return null;
    if (task.type === "recurring" && iso) {
        const ov = effectiveOverrides(task).find((o) => o.to === iso);
        if (ov && ov.to_time) return ov.to_time;
    }
    return task.time || null;
};

/** Build a `{ iso → count }` map for `tasks` across the given date range (inclusive both ends).
 *  Uses `taskDayOffset` so recurring tasks are counted exactly once (at their next occurrence)
 *  — matching the phone screensaver's DayStrip counter. Archived tasks are skipped. */
export const perDayTaskCounts = (tasks, startIso, endIso) => {
    const startDate = dateFromStr(startIso);
    const endDate = dateFromStr(endIso);
    if (!startDate || !endDate) return {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOff = Math.round((startDate - today) / 86_400_000);
    const endOff = Math.round((endDate - today) / 86_400_000);
    const counts = {};
    for (const t of tasks || []) {
        if (t.archived) continue;
        const off = taskDayOffset(t);
        if (off === null || off < startOff || off > endOff) continue;
        const d = new Date(today);
        d.setDate(today.getDate() + off);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        counts[iso] = (counts[iso] || 0) + 1;
    }
    return counts;
};

export const formatDate = (dateStr) => {
    const d = dateFromStr(dateStr);
    if (!d) return "";
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

export const formatTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    if (isNaN(h)) return "";
    const ampm = h >= 12 ? "PM" : "AM";
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
};

export const cn = (...args) => args.filter(Boolean).join(" ");

export const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

export const isImageMime = (name) => /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(name);

export const processFile = (file) =>
    new Promise((resolve, reject) => {
        // Base64 payload ceiling — Mongo docs cap at 16 MB, so we stay well under.
        // 5.5 MB of base64 ≈ ~4 MB of raw file, plenty for normal PDFs and docx.
        const MAX_BASE64 = 5_500_000;
        if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    let { width, height } = img;
                    const MAX = 1200;
                    if (width > height && width > MAX) {
                        height *= MAX / width;
                        width = MAX;
                    } else if (height > MAX) {
                        width *= MAX / height;
                        height = MAX;
                    }
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve({ type: "image", name: file.name, data: canvas.toDataURL("image/jpeg", 0.7) });
                };
                img.src = e.target.result;
            };
            reader.onerror = () => reject(new Error(`read failed: ${file.name}`));
            reader.readAsDataURL(file);
        } else {
            const reader = new FileReader();
            reader.onload = (e) => {
                const d = e.target.result;
                if (d.length > MAX_BASE64) {
                    const mb = (file.size / 1_048_576).toFixed(1);
                    reject(new Error(`"${file.name}" is ${mb} MB — max 4 MB`));
                    return;
                }
                // Detect PDFs by MIME or filename so the lightbox can render them inline.
                const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
                resolve({ type: isPdf ? "pdf" : "file", name: file.name, data: d });
            };
            reader.onerror = () => reject(new Error(`read failed: ${file.name}`));
            reader.readAsDataURL(file);
        }
    });

// Matches http(s):// URLs, www-prefixed URLs, AND bare domains like "google.com" or
// "example.co.uk/path". The negative lookbehind `(?<![@\w.])` prevents matching inside
// emails ("foo@bar.com") and mid-word snippets ("e.g." or "hello.world.foo").
const URL_RE = /(?<![@\w.])((?:https?:\/\/|www\.)\S+|(?:[a-z0-9-]+\.)+(?:com|net|org|io|ai|co|dev|app|us|ca|uk|au|gov|edu|info|biz|tv|me|xyz|so|to|nz|de|fr|jp|cn|in|pl|nl|es|it|br|mx|se|no|fi|ru|za|ke|il|sg|hk|kr|be|ch|at|dk|ie|pt|gr|cz|tr|tw|vn|ph|th|id|my|ar|cl|ng|eg|ma|sa|ae)(?![a-z0-9-])(?:\/\S*)?)/gi;
// Strip trailing punctuation so "Read google.com." doesn't include the period.
const stripUrlTail = (s) => s.replace(/[.,;:!?)\]}'"]+$/, "");

/** A URL tap must never start a swipe/drag, toggle, open the editor or count as a Screensaver
 *  tap — stop every gesture event at the anchor. Native navigation (href/target) still happens. */
const stopGesture = (e) => { e.stopPropagation(); };
/** True when a DOM event originated inside a rendered link. */
export const isLinkTarget = (target) => !!(target && typeof target.closest === "function" && target.closest("a[href]"));

/** Plain URL list from free text (https:// · http:// · www. · bare domains) — used by the editor's
 *  read-only Links area. Returns hrefs, deduped, in order of appearance. */
export const extractUrls = (text) => {
    const str = String(text || "");
    const out = [];
    URL_RE.lastIndex = 0;
    let m;
    while ((m = URL_RE.exec(str)) !== null) {
        const clean = stripUrlTail(m[1]);
        if (!clean) continue;
        const href = /^https?:/i.test(clean) ? clean : `https://${clean}`;
        if (!out.some((o) => o.href === href)) out.push({ href, label: clean });
    }
    return out;
};

/** Which obligation an "edit" on an Overdue recurring row means. Shared by the Overdue list and the
 *  Screensaver so both surfaces behave identically: 0 misses → none · 1 → that exact source key ·
 *  2+ → the caller must open the picker (never guess). */
export const overdueEditTarget = (task, now = new Date()) => {
    if (!task) return { kind: "none", task: null };
    if (task.type !== "recurring" || !task.interval) return { kind: "editor", task: { ...task, _occurrenceDate: task.date || todayStr() } };
    const misses = task._missedOccurrences || getMissedOccurrences(task, now);
    if (!misses.length) return { kind: "none", task };
    if (misses.length > 1) return { kind: "pick", task: { ...task, _missedOccurrences: misses, _missedCount: misses.length, _needsOccurrencePick: true } };
    const key = misses[0];
    const display = effectiveOverrides(task).find((o) => o.from === key)?.to || key;
    return { kind: "editor", task: { ...task, _occKey: key, _displayIso: display, _occurrenceDate: display } };
};

/** SSE guard: an incoming task payload is STALE when both sides carry a `rev` and the payload's
 *  is lower than what we already hold. Stale payloads must never be applied. */
export const isStaleRevision = (held, incoming) =>
    held?.rev != null && incoming?.rev != null && incoming.rev < held.rev;

/** Upsert `doc` into `prev` by id; returns `prev` untouched when the payload is stale. */
export const applyUpsert = (prev, doc) => {
    if (!doc || !doc.id) return prev;
    const i = prev.findIndex((x) => x.id === doc.id);
    if (i === -1) return [...prev, doc];
    if (isStaleRevision(prev[i], doc)) return prev;
    const next = prev.slice();
    next[i] = doc;
    return next;
};

/** Milliseconds until the next LOCAL midnight (device clock / tz, DST-safe via Date arithmetic). */
export const msUntilNextLocalMidnight = (now = new Date()) => {
    const next = new Date(now);
    next.setHours(24, 0, 0, 0);
    return Math.max(1000, next.getTime() - now.getTime());
};

/** Where a SOURCE obligation is actually shown/due after the canonical override (its display day). */
export const effectiveDisplayIso = (task, sourceIso) =>
    effectiveOverrides(task).find((o) => o.from === sourceIso)?.to || sourceIso;

/** True when completing `sourceIso` now would be a FUTURE write: its effective display day is
 *  after the local calendar day. A future source legitimately moved onto today is NOT future. */
export const isFutureObligation = (task, sourceIso, today = todayStr()) => effectiveDisplayIso(task, sourceIso) > today;

/** The ONE occurrence a generic "check in" on a recurring series may act on — never a future beat.
 *  Order: unresolved overdue (1 → that key, 2+ → caller must open the exact-occurrence picker),
 *  then today's open obligation, else nothing (`kind: "none"`). Identity is always the SOURCE key. */
export const actionableOccurrence = (task, now = new Date()) => {
    if (!task || task.type !== "recurring" || !task.interval) return { kind: "none", key: null, displayIso: null, missed: [] };
    const missed = getMissedOccurrences(task, now);
    if (missed.length > 1) return { kind: "pick", key: null, displayIso: null, missed };
    if (missed.length === 1) return { kind: "overdue", key: missed[0], displayIso: effectiveDisplayIso(task, missed[0]), missed };
    const today = todayStr(now);
    const done = task.completions || [];
    const open = getOccurrencesOnIso(task, today).find((o) => !done.includes(o.key) && !isOccurrenceSnoozed(task, o.key, now.getTime()));
    if (open) return { kind: "today", key: open.key, displayIso: today, missed };
    return { kind: "none", key: null, displayIso: null, missed };
};

/** Display-only: the next SCHEDULED day of the series strictly after today (pure schedule, ignores
 *  completion state). Never used as a write key. */
export const nextScheduledIso = (task, now = new Date()) => {
    if (!task || task.type !== "recurring" || !task.interval) return null;
    const d = new Date(now); d.setHours(0, 0, 0, 0);
    for (let off = 1; off <= 400; off++) {
        d.setDate(d.getDate() + 1);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        if (getOccurrencesOnIso(task, iso).length) return iso;
    }
    return null;
};


/** Walk `text` and interleave <a> nodes for every URL-ish token. Optional `renderText`
 *  callback lets callers (linkifyHighlight) wrap the non-URL segments with highlighted marks. */
const buildLinkNodes = (text, renderText = (s, k) => <span key={k}>{s}</span>) => {
    const str = String(text || "");
    const nodes = [];
    let lastEnd = 0;
    let idx = 0;
    let m;
    URL_RE.lastIndex = 0;
    while ((m = URL_RE.exec(str)) !== null) {
        const raw = m[1];
        const clean = stripUrlTail(raw);
        if (!clean) continue;
        const start = m.index;
        const end = start + clean.length;
        if (start > lastEnd) nodes.push(renderText(str.slice(lastEnd, start), `t-${idx++}`));
        const href = /^https?:/i.test(clean) ? clean : `https://${clean}`;
        nodes.push(
            <a
                key={`u-${idx++}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                draggable={false}
                className="text-meye-cyan underline underline-offset-2 pointer-events-auto"
                data-testid="safe-link"
                onPointerDown={stopGesture}
                onMouseDown={stopGesture}
                onTouchStart={stopGesture}
                onClick={stopGesture}
            >
                {clean}
            </a>,
        );
        lastEnd = end;
    }
    if (lastEnd < str.length) nodes.push(renderText(str.slice(lastEnd), `t-${idx++}`));
    return nodes;
};

export const linkify = (text) => {
    if (!text) return null;
    return buildLinkNodes(text);
};

/** Wrap every case-insensitive occurrence of `term` inside `text` with a highlighted <mark>.
 *  Multi-word queries: each unique whitespace-separated token AND the full-phrase are highlighted
 *  (phrase first so it wins the split, then individual words in remaining unmatched slices). */
export const highlight = (text, term) => {
    const str = String(text || "");
    const raw = String(term || "").trim();
    if (!raw) return str;
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Deduped, longest-first so "back pain" matches before "back" grabs it alone
    const tokens = Array.from(new Set(
        raw.split(/\s+/).filter(Boolean).concat(raw.includes(" ") ? [raw] : []),
    )).sort((a, b) => b.length - a.length);
    if (tokens.length === 0) return str;
    const re = new RegExp(`(${tokens.map(esc).join("|")})`, "ig");
    const parts = str.split(re);
    return parts.map((p, i) => {
        // Odd-indexed parts are the captured matches (guaranteed by the alternation)
        if (i % 2 === 1) {
            return (
                <mark
                    key={i}
                    className="bg-meye-cyan/40 text-white rounded px-0.5"
                >
                    {p}
                </mark>
            );
        }
        return <span key={i}>{p}</span>;
    });
};

/** Same as `linkify`, but ALSO highlights `term` inside plain-text segments. */
export const linkifyHighlight = (text, term) => {
    if (!text) return null;
    if (!term) return linkify(text);
    return buildLinkNodes(text, (chunk, k) => <span key={k}>{highlight(chunk, term)}</span>);
};



/** Body/description preview for task rows. Defensive guard: never repeats the title — if a
 *  legacy/AI body starts with the title ("Letter for Tunity. Hello…") that prefix is dropped.
 *  Display-only; the stored `description` is never truncated or rewritten here. */
export const taskBodyPreview = (task) => {
    const body = (task?.description || "").replace(/\s+/g, " ").trim();
    if (!body) return "";
    const title = (task?.text || "").replace(/\s+/g, " ").trim().replace(/[.:;,!?\-\s]+$/, "").toLowerCase();
    if (title && body.toLowerCase().startsWith(title)) {
        return body.slice(title.length).replace(/^[\s.:;,!?\-—–]+/, "");
    }
    return body;
};

const SPLIT_ABBREV = new Set(["dr", "mr", "mrs", "ms", "st", "vs", "etc", "jr", "sr", "no", "inc", "ltd", "co", "mt", "ave", "apt", "e.g", "i.e", "approx", "dept", "est"]);
/** Mirrors backend `_propose_split`: conservative heading/body split for legacy combined text. */
export const proposeSplit = (text) => {
    const t = (text || "").trim();
    if (t.length < 80) return null;
    const pat = /\n|(?<=[.!?:])\s+|\s[—–-]\s/g;
    let m;
    while ((m = pat.exec(t)) !== null) {
        const rawHead = t.slice(0, m.index).trim();
        const head = rawHead.replace(/[.:;,!?\-—–\s]+$/, "");
        const body = t.slice(m.index + m[0].length).trim();
        if (head.length > 70) return null;
        const lastWord = head.toLowerCase().split(/\s+/).pop() || "";
        if (head.length >= 3 && head.split(/\s+/).length >= 2 && body.length >= 20 && !SPLIT_ABBREV.has(lastWord) && !/\d$/.test(head)) {
            return { title: head, description: body, confidence: m[0].includes("\n") || rawHead.endsWith(":") ? "high" : "medium" };
        }
    }
    return null;
};
